import React, {useState, useEffect, useRef} from 'react';
import {
  ScrollView,
  View,
  Image,
  ImageBackground,
  Text,
  Pressable,
} from 'react-native';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {Submission} from '../../Models/PowerSyncModels/models';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import {TemplateCategoryTypes} from '../../Constants/TemplateCategoryTypes';
import {Navigation} from '../../Constants/Navigation';
import {
  METADATA_KEYS,
  CATEGORY,
  PERMISSIONS,
} from '../../Constants/AppConstants';
import RecordList from '../Shared/RecordList';
import {useRelation} from '../../Providers/SubmissionRelationProvider';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {Images} from '../../Constants/Images';
import {useIsFocused} from '@react-navigation/native';
import {usePermission} from '../../Providers/PermissionProvider';
import {FilterTypes} from '../../Constants/General';
import ScreenButtons from '../Shared/ScreenButtons';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import {FilterValue} from '../Shared/SearchBox';

export declare type EquipmentProps = {
  navigation: any;
};

const Equipment = (props: EquipmentProps): React.ReactElement => {
  const [reports, setReports] = useState<Submission[]>([]);
  const [filterValue, setFilterValue] = useState<FilterValue>({
    startDate: '',
    endDate: '',
    statuses: [],
    types: [],
    person: '',
    project: '',
    equipmentType: '',
    equipment: '',
    pendingInvestigation: false,
    investigationAttached: false,
  });

  const isFocused = useIsFocused();
  const {hasPermission} = usePermission();

  const {getRelatedSubmissions} = useRelation();
  const {templateCategories, people, templates, submissions, powersyncReady} = usePowerSyncData();

  const user = usePowerSyncUser();

  const unselectCommand = useRef({
    unselect: () => {},
    setUnselectCommand(func: () => void) {
      this.unselect = func;
    },
  });

  useEffect(() => {
    fetchReports();
  }, [powersyncReady]);

  useEffect(() => {
    if (isFocused) {
      fetchReports();
    }
  }, [isFocused]);

  const fetchReports = async () => {
    let viewReport = hasPermission(PERMISSIONS.REPORT_EQUIPMENT_VIEW);

    let templateCategory = templateCategories
      .find(t => t.name === TemplateCategoryTypes.EQUIPMENT);
      
    let listTemplateIds = templateCategory?.listTemplateIds ?? [];

    let equipmentTemplates = templates
      .filter(
        t =>
          listTemplateIds.includes(t._id)
      )
      .map(t => t.name.toLowerCase().trim());

    let subs = submissions.filter(
      s =>
        equipmentTemplates.includes(
          s.templateVersion.name?.toLowerCase().trim() ?? '',
        ) &&
        (s.submissionStatus.name === SubmissionStatuses.DRAFT ||
          s.submissionStatus.name === SubmissionStatuses.SUBMITTED),
    );

    //If no view permissions can only see forms that user created or that they can manage
    if (!viewReport) {
      //Special permissions for PM/Supervisor
      let userInfo = user.getUserInfo();
      if (userInfo) {
        let userEmail = userInfo.email;
        let personLoggedIn = people.find(x => x.email == userEmail);

        if (personLoggedIn) {
          let submissionIds = await getRelatedSubmissions(personLoggedIn._id);
          subs = subs.filter(
            x => submissionIds.includes(x._id) || x.createdBy == userInfo.email,
          );
        }
      }
    }

    setReports(subs);
  };

  if (!isFocused) return <></>;

  async function recordPressed(recordId: string): Promise<void> {
    props.navigation.push(Navigation.DYNAMICFORM, {
      submissionId: recordId,
      showConfirmation: false,
    });
  }

  return (
    <ScrollView style={{backgroundColor: 'white'}}>
      <Pressable
        style={{
          //@ts-ignore
          cursor: 'auto',
        }}
        onPress={() => {
          unselectCommand.current.unselect();
        }}>
        <>
          <View>
            <ImageBackground
              source={Images.EQUIPMENT}
              resizeMode='cover'
              style={{height: 260}}>
              <Text style={ReportsStyleSheet.headerTitle}>Equipment</Text>
            </ImageBackground>
          </View>
          <View style={{flexDirection: 'row'}}>
            <View style={{flex: 2, padding: 28}}>
              <RecordList
                records={reports}
                showDrafts={true}
                hasMetaData={true}
                category={CATEGORY.EQUIPMENT}
                metaDataKey_title={METADATA_KEYS.EQUIPMENTID}
                metaDataKey_project={METADATA_KEYS.PROJECT_NAME}
                showFilters={true}
                filters={[
                  {label: 'Date Range', type: FilterTypes.DATE_RANGE},
                  {label: 'Type', type: FilterTypes.REPORT_TYPE},
                  {label: 'Project/Site', type: FilterTypes.PROJECT},
                  {label: '', type: FilterTypes.EQUIPMENT},
                ]}
                navigation={props.navigation}
                onRecordPressed={recordId => recordPressed(recordId)}
                setUnselectCommand={(func: () => void) => {
                  unselectCommand.current.setUnselectCommand(func);
                }}
                filterValue={filterValue}
                onChangeFilter={filterValue => setFilterValue(filterValue)}
              />
            </View>
            <View style={{flex: 1}}>
              <ScreenButtons
                navigation={props.navigation}
                category={TemplateCategoryTypes.EQUIPMENT}
                permissions={PERMISSIONS.REPORT_EQUIPMENT_CREATE}
              />
              <View style={CommonStyleSheet.bcsfFooter}>
                <Image
                  source={Images.BCSF}
                  style={CommonStyleSheet.bcsfFooterImage}
                />
                <Text style={CommonStyleSheet.bcsfFooterText}>
                  BC <Text style={{fontWeight: '700'}}>Forest Safety</Text>
                </Text>
              </View>
            </View>
          </View>
        </>
      </Pressable>
    </ScrollView>
  );
};

export default Equipment;
